// see: https://www.sammeechward.com/cognito-user-pool-react

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  ISignUpResult,
} from 'amazon-cognito-identity-js';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

type UserData = {
  username: string;
  email: string;
  [key: string]: unknown;
};
type NewPasswordResponse = {
  currentUser: CognitoUser;
  userAttributes: Record<string, string | undefined>;
  requiredAttributes: string[];
};

const userPool = new CognitoUserPool({
  UserPoolId: `${import.meta.env.COGNITO_USER_POOL_ID}`,
  ClientId: `${import.meta.env.COGNITO_CLIENT_ID}`,
  // Storage: sessionStorage, //default is localStorage
});

let currentUser: CognitoUser | undefined;
const getCurrentUser = () => {
  // console.log('currentUser', currentUser);
  if (currentUser) return currentUser;

  // is the current user in localStorage?
  const userPoolLocalStorage = new CognitoUserPool({
    UserPoolId: `${import.meta.env.COGNITO_USER_POOL_ID}`,
    ClientId: `${import.meta.env.COGNITO_CLIENT_ID}`,
    Storage: localStorage,
  });
  const userLocalStorage = userPoolLocalStorage.getCurrentUser();
  // console.log('userLocalStorage', userLocalStorage);
  if (userLocalStorage) {
    currentUser = userLocalStorage;
    return userLocalStorage;
  }

  // is the current user in sessionStorage?
  const userPoolSessionStorage = new CognitoUserPool({
    UserPoolId: `${import.meta.env.COGNITO_USER_POOL_ID}`,
    ClientId: `${import.meta.env.COGNITO_CLIENT_ID}`,
    Storage: sessionStorage,
  });
  const userSessionStorage = userPoolSessionStorage.getCurrentUser();
  // console.log('userSessionStorage', userSessionStorage);
  if (userSessionStorage) {
    currentUser = userSessionStorage;
    return userSessionStorage;
  }
  return undefined;
};

const signUp = (email: string, password: string, givenName = '', familyName = ''): Promise<ISignUpResult> => {
  const username = email;
  const trimmedGivenName = givenName.trim();
  const trimmedFamilyName = familyName.trim();
  const fullName = [trimmedGivenName, trimmedFamilyName].filter(Boolean).join(' ');

  const attributeList = [
    new CognitoUserAttribute({
      Name: 'email',
      Value: email,
    }),
  ];

  if (trimmedGivenName) {
    attributeList.push(
      new CognitoUserAttribute({
        Name: 'given_name',
        Value: trimmedGivenName,
      })
    );
  }

  if (trimmedFamilyName) {
    attributeList.push(
      new CognitoUserAttribute({
        Name: 'family_name',
        Value: trimmedFamilyName,
      })
    );
  }

  if (fullName) {
    attributeList.push(
      new CognitoUserAttribute({
        Name: 'name',
        Value: fullName,
      })
    );
  }

  return new Promise((resolve, reject) => {
    userPool.signUp(username, password, attributeList, [], (error, result) => {
      if (result) resolve(result);
      else if (error) reject(error);
      else reject(new Error('Failed user sign-up'));
    });
  });
};

const confirmSignUp = (username: string, code: string): Promise<boolean> => {
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });
  return new Promise((resolve, reject) => {
    cognitoUser.confirmRegistration(code, true, (error, result) => {
      if (result === 'SUCCESS') resolve(true);
      else if (error) reject(error);
      else reject(new Error("Failed to confirm users' sign-up"));
    });
  });
};

const signIn = (
  username: string,
  password: string,
  rememberMe: boolean = false
): Promise<CognitoUserSession | NewPasswordResponse> => {
  const authenticationDetails = new AuthenticationDetails({
    Username: username,
    Password: password,
  });

  currentUser = new CognitoUser({
    Username: username,
    Pool: userPool,
    Storage: rememberMe ? localStorage : sessionStorage,
  });
  return new Promise((resolve, reject) => {
    if (!currentUser) {
      reject(new Error('No user found.'));
      return;
    }
    currentUser.authenticateUser(authenticationDetails, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (error) => reject(error),
      newPasswordRequired: (userAttributes: Record<string, string | undefined>, requiredAttributes: string[]) => {
        if (!currentUser) {
          reject(new Error('No user found.'));
          return;
        }
        resolve({
          currentUser,
          userAttributes,
          requiredAttributes,
        });
      },
    });
  });
};

const forgotPassword = (username: string): Promise<void> => {
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (error) => {
        reject(error);
      },
      // inputVerificationCode: (data) => {
      //   console.log('data', data);
      //   console.log('code sent to email');
      //   resolve();
      // },
      //
      // Optional callback to tell how the code was sent
      // {
      //   "CodeDeliveryDetails": {
      //       "AttributeName": "email",
      //       "DeliveryMedium": "EMAIL",
      //       "Destination": "p***@g***"
      // }
    });
  });
};

const confirmPassword = (username: string, code: string, newPassword: string): Promise<void> => {
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool,
  });

  return new Promise((resolve, reject) => {
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (error) => {
        reject(error);
      },
    });
  });
};

// To update the a forced update on first login, we need to pass the CognitoUser object
// to the completeNewPasswordChallenge method
// This is a workaround to pass the CognitoUser object around...
const completeNewPassword = (
  initiatingUser: CognitoUser,
  requiredAttributeData: string[],
  newPassword: string
): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) => {
    initiatingUser.completeNewPasswordChallenge(newPassword, requiredAttributeData, {
      onSuccess: (session) => {
        resolve(session);
      },
      onFailure: (error) => {
        reject(error);
      },
    });
  });

const changePassword = (oldPassword: string, newPassword: string): Promise<void> => {
  const cognitoUser = getCurrentUser();
  if (!cognitoUser) {
    return Promise.reject(new Error('No user found.'));
  }

  return new Promise((resolve, reject) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cognitoUser.changePassword(oldPassword, newPassword, (error, _result) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
  });
};

const getUser = async (): Promise<UserData> =>
  new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    // console.log('cognitoUser', cognitoUser);

    if (!cognitoUser) {
      reject(new Error('No user found'));
      return;
    }

    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (error) {
        reject(error);
        return;
      }
      if (!session) {
        reject(new Error('No session found.'));
        return;
      }

      cognitoUser.getUserAttributes((error: Error | undefined, attributes: CognitoUserAttribute[] | undefined) => {
        if (error) {
          reject(error);
          return;
        }
        const userData = attributes?.reduce((acc: Record<string, unknown>, attribute) => {
          acc[attribute.Name] = attribute.Value;
          return acc;
        }, {});

        const logUserId: string = cognitoUser.getUsername();
        const logUsername: string = (userData?.name || '') as string;
        const logEmail: string = (userData?.email || '') as string;
        Sentry.setUser({
          username: logUsername,
          email: logEmail,
          id: logUserId,
        });
        posthog.identify(logUserId, {
          email: logUsername,
          name: logEmail,
        });
        posthog.register({ build: `${import.meta.env.COMMIT_HASH}`, env: import.meta.env.ENV });
        posthog.setPersonProperties({ last_session: new Date().toISOString(), env: import.meta.env.ENV });
        resolve({ ...userData, username: cognitoUser.getUsername() } as UserData);
      });
    });
  });

const getSession = (): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) => {
    const cognitoUser = getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('Not logged in. No user found.'));
      return;
    }
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) => {
      if (session) resolve(session);
      else if (error) reject(error);
      else reject(new Error('Failed to get user session.'));
    });
  });

const signOut = (): void => {
  if (currentUser) currentUser.signOut();
  // belt and suspenders
  localStorage.clear();
  sessionStorage.clear();
};

// ID token us used to authenticate to other services
const getIdToken = async () => {
  try {
    const session = await getSession();
    const jwtToken = session.getIdToken().getJwtToken();
    // console.log('jwt ID Token: ', jwtToken);
    return jwtToken;
  } catch {
    return null;
  }
};

// access token is used for cognito api call (refresh, etc)
const getAccessToken = async () => {
  const session = await getSession();
  const jwtToken = session.getAccessToken().getJwtToken();
  return jwtToken;
};

// Decode a JWT payload (browser-safe)
const decodeJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1] || ''));
  } catch {
    return {};
  }
};

const getUserGroups = async (): Promise<string[]> => {
  try {
    const idToken = await getIdToken();
    if (!idToken) return [];
    const payload = decodeJwt(idToken) as Record<string, unknown>;
    return (payload['cognito:groups'] as string[] | undefined) ?? [];
  } catch {
    return [];
  }
};

const isInGroup = async (groupName: string): Promise<boolean> => {
  const groups = await getUserGroups();
  return groups.includes(groupName);
};

export {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  forgotPassword,
  confirmPassword,
  changePassword,
  completeNewPassword,
  getSession,
  getUser,
  getIdToken,
  getAccessToken,
  getUserGroups,
  isInGroup,
};

export type { UserData };
